import { useApolloClient } from '@apollo/client';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { MANAGEMENT_ROLE_ID, useAnalytics } from '@treams/treams-analytics';
import { useLocalize } from '@treams/treams-hooks';
import {
	TalentReviewAnalyticsGroupDocument,
	type TalentReviewAnalyticsGroupQuery,
	type TalentReviewAnalyticsGroupQueryVariables,
	TalentReviewTemplateDocument,
	type TalentReviewTemplateFragment,
	type TalentReviewTemplateQuery,
	type TalentReviewTemplateQueryVariables,
} from '@treams/treams-network';
import { type TalentReviewGridCell } from '@treams/treams-ui';

import {
	type RetentionRiskPerson,
	type RetentionRiskUser,
	retentionRiskPeople,
} from '../../talent-reviews/v2/retentionRisk';
import { dayOf, supersedes } from '../../talent-reviews/v2/talentReviewWriteOrder';
import { type TalentReviewExportEmployee } from './talentReviewExcelExport';

export type TalentReviewCellEmployee = TalentReviewExportEmployee & {
	avatar?: { source: { url: string } } | null;
};

/**
 * Turns cell counts into whole percentages that add up to 100. Remainder points go to the cells with the largest leftover fraction.
 * @param counts cell counts in grid order
 */
export function shares(counts: number[]): number[] {
	const total = counts.reduce((sum, count) => sum + count, 0);

	if (total === 0) {
		return counts.map(() => 0);
	}

	const exact = counts.map((count) => (count / total) * 100);
	const floors = exact.map((value) => Math.floor(value));
	let remainder = 100 - floors.reduce((sum, value) => sum + value, 0);

	const order = exact
		.map((value, index) => ({ index, fraction: value - floors[index] }))
		.sort((left, right) => right.fraction - left.fraction);

	for (const { index } of order) {
		if (remainder <= 0) {
			break;
		}

		floors[index] += 1;
		remainder -= 1;
	}

	return floors;
}

type Placement = {
	userID: string;
	row: number;
	column: number;
	groupID: string;
	localDate: string;
};

type Member = {
	id: string;
	firstName: string;
	lastName: string;
	jobTitle: string;
	managerName: string;
	roleIDs: string[];
	avatar: TalentReviewCellEmployee['avatar'];
};

export type TalentReviewAnalyticsCell = TalentReviewGridCell & {
	employees: TalentReviewCellEmployee[];
};

const personName = (person: { firstName: string; lastName: string }) =>
	`${person.firstName} ${person.lastName}`.trim();

const cellKey = (row: number, column: number) => `${row}:${column}`;

export type TalentReviewDepartmentRow = {
	id: string;
	name: string;
	managerName: string;
	placedCount: number;
	eligibleCount: number;
};

export type TalentReviewAnalyticsResult = {
	rows: TalentReviewAnalyticsCell[][];
	xAxisLabel: string;
	yAxisLabel: string;
	hasNorm: boolean;
	placedCount: number;
	eligibleCount: number;
	previousPlacedCount: number;
	previousEligibleCount: number;
	departments: TalentReviewDepartmentRow[];
	retention: RetentionRiskPerson[];
};

const isEligible = (member: Member, roleID?: string) => !roleID || member.roleIDs.includes(roleID);

const countsFor = (
	cells: TalentReviewTemplateFragment['cells'],
	placements: Placement[],
): number[] => {
	const counts = cells.flatMap((row) => row.map(() => 0));

	for (const placement of placements) {
		const index =
			cells.slice(0, placement.row).reduce((sum, row) => sum + row.length, 0) + placement.column;

		if (index >= 0 && index < counts.length) {
			counts[index] += 1;
		}
	}

	return counts;
};

const byEmployeeName = (left: TalentReviewCellEmployee, right: TalentReviewCellEmployee) => {
	const byName = `${left.lastName} ${left.firstName}`.localeCompare(
		`${right.lastName} ${right.firstName}`,
		undefined,
		{ sensitivity: 'base' },
	);

	return byName || left.id.localeCompare(right.id);
};

const toGridRows = ({
	template,
	currentCounts,
	previousCounts,
	employees,
	localize,
	deltaTooltip,
}: {
	template: TalentReviewTemplateFragment;
	currentCounts: number[];
	previousCounts?: number[];
	employees: Map<string, TalentReviewCellEmployee[]>;
	localize: ReturnType<typeof useLocalize>['localize'];
	deltaTooltip: string;
}): TalentReviewAnalyticsCell[][] => {
	const currentShares = shares(currentCounts);
	const previousShares = previousCounts ? shares(previousCounts) : undefined;
	let index = 0;

	const rows = template.cells.map((row, rowIndex) =>
		row.map((cell, columnIndex) => {
			const value = currentShares[index] ?? 0;
			const previousValue = previousShares?.[index];
			index += 1;

			return {
				title: localize(cell.label)?.text ?? '',
				value,
				norm: cell.norm,
				previousValue,
				deltaTooltip: previousValue === undefined ? undefined : deltaTooltip,
				employees: (employees.get(cellKey(rowIndex, columnIndex)) ?? []).sort(byEmployeeName),
			};
		}),
	);

	return rows.reverse();
};

const managerName = (
	managers: Array<{ role: { id: string }; node: { firstName: string; lastName: string } }>,
) => {
	const primary = managers.find(
		(edge) => edge.role.id === MANAGEMENT_ROLE_ID.PRIMARY_MANAGER,
	)?.node;

	if (!primary) {
		return '';
	}

	return personName(primary);
};

const memberFromAnalyticsGroup = (
	node: TalentReviewAnalyticsGroup['members']['edges'][number]['node'],
	localize: ReturnType<typeof useLocalize>['localize'],
): Member => {
	const primary = node.individualManagers.find(
		(edge) => edge.role.id === MANAGEMENT_ROLE_ID.PRIMARY_MANAGER,
	)?.source;

	return {
		id: node.id,
		firstName: node.firstName,
		lastName: node.lastName,
		jobTitle: localize(node.currentJobPosition?.jobTitle.title)?.text ?? '',
		managerName: primary ? personName(primary) : '',
		roleIDs: node.individualManagers.map((edge) => edge.role.id),
		avatar: node.avatar ? { source: { url: node.avatar.source.url } } : null,
	};
};

const uniqueByUser = (placements: Placement[]) => {
	const latest = new Map<string, Placement>();

	for (const placement of placements) {
		if (!latest.has(placement.userID)) {
			latest.set(placement.userID, placement);
		}
	}

	return [...latest.values()];
};

/** A placement write as stored in the analytics v3 `TalentReviewPlacements` store. */
export type PlacementWrite = {
	userID: string;
	groupID: string;
	/** Absent on older writes; their day is the date of `createdAt`. */
	localDate?: string;
	row: number;
	column: number;
	removed?: number;
	retentionRisk?: string | null;
	createdAt: Date;
};

/**
 * One group's grid on one day, built like the talent review service does:
 * per person the first write up to `localDate` in grid order (latest day,
 * then newest write). People whose latest write removed them drop out.
 * @param writes the group's placement writes, in any order
 * @param localDate the viewed day, YYYY-MM-DD
 * @param eligibleIDs the people that count for this group
 */
export function placementsOn(
	writes: PlacementWrite[],
	localDate: string,
	eligibleIDs: Set<string>,
): Placement[] {
	const latest = new Map<string, PlacementWrite>();

	for (const write of writes) {
		if (dayOf(write) > localDate || !eligibleIDs.has(write.userID)) {
			continue;
		}

		const current = latest.get(write.userID);

		if (!current || supersedes(write, current)) {
			latest.set(write.userID, write);
		}
	}

	return [...latest.values()]
		.filter((write) => !write.removed)
		.map((write) => ({
			userID: write.userID,
			row: write.row,
			column: write.column,
			groupID: write.groupID,
			localDate: dayOf(write),
		}));
}

// Event rows (`type`) and corrections of a submitted snapshot (`snapshotID`)
// never count for the live grid.
const placementsPipeline = (templateID: string, groupIDs: string[]) => [
	{ $match: { templateID, groupID: { $in: groupIDs }, type: null, snapshotID: null } },
	{
		$project: {
			userID: 1,
			groupID: 1,
			localDate: 1,
			row: 1,
			column: 1,
			removed: 1,
			retentionRisk: 1,
			createdAt: 1,
		},
	},
	{ $sort: { createdAt: -1 } },
];

type TalentReviewAnalyticsGroup = TalentReviewAnalyticsGroupQuery['group'];

/** Everything the page needs for any day: switching periods only recomputes. */
type TalentReviewAnalyticsSource = {
	key: string;
	template: TalentReviewTemplateFragment;
	groups: TalentReviewAnalyticsGroup[];
	writesByGroup: Map<string, PlacementWrite[]>;
};

async function loadTalentReviewAnalyticsSource({
	client,
	requestQuery,
	templateID,
	groupIDs,
}: {
	client: ReturnType<typeof useApolloClient>;
	requestQuery: ReturnType<typeof useAnalytics>['requestQuery'];
	templateID: string;
	groupIDs: string[];
}): Promise<Omit<TalentReviewAnalyticsSource, 'key'>> {
	const [templateResult, groupResults, rows] = await Promise.all([
		client.query<TalentReviewTemplateQuery, TalentReviewTemplateQueryVariables>({
			query: TalentReviewTemplateDocument,
			variables: { id: templateID },
		}),
		Promise.all(
			groupIDs.map((groupID) =>
				client.query<TalentReviewAnalyticsGroupQuery, TalentReviewAnalyticsGroupQueryVariables>({
					query: TalentReviewAnalyticsGroupDocument,
					variables: { id: groupID },
				}),
			),
		),
		requestQuery('TalentReviewPlacements', placementsPipeline(templateID, groupIDs)),
	]);

	const writes = rows as unknown as PlacementWrite[];
	const writesByGroup = new Map<string, PlacementWrite[]>(groupIDs.map((id) => [id, []]));

	for (const row of writes) {
		writesByGroup.get(row.groupID)?.push(row);
	}

	return {
		template: templateResult.data.talentReviewTemplate,
		groups: groupResults.map((result) => result.data.group),
		writesByGroup,
	};
}

function computeTalentReviewAnalytics({
	source,
	chartGroupIDs,
	roleID,
	localDate,
	previousLocalDate,
	localize,
	deltaTooltip,
}: {
	source: TalentReviewAnalyticsSource;
	chartGroupIDs: string[];
	roleID?: string;
	localDate: string;
	previousLocalDate?: string;
	localize: ReturnType<typeof useLocalize>['localize'];
	deltaTooltip: string;
}): TalentReviewAnalyticsResult {
	const { template } = source;

	const perGroup = source.groups.map((group) => {
		const members = group.members.edges.map(({ node }) => memberFromAnalyticsGroup(node, localize));
		const eligibleIDs = new Set(
			members.filter((member) => isEligible(member, roleID)).map((member) => member.id),
		);
		const writes = source.writesByGroup.get(group.id) ?? [];

		return {
			group,
			members: new Map(members.map((member) => [member.id, member])),
			departmentManager: managerName(group.managers.edges),
			eligibleIDs,
			placements: placementsOn(writes, localDate, eligibleIDs),
			previousPlacements: previousLocalDate
				? placementsOn(writes, previousLocalDate, eligibleIDs)
				: [],
		};
	});

	const chartGroups = new Set(chartGroupIDs);
	const forChart = perGroup.filter(({ group }) => chartGroups.has(group.id));
	const eligibleIDs = new Set(forChart.flatMap(({ eligibleIDs }) => [...eligibleIDs]));
	const users = new Map<string, RetentionRiskUser>();

	for (const { group } of forChart) {
		for (const { node } of group.members.edges) {
			users.set(node.id, {
				firstName: node.firstName,
				lastName: node.lastName,
				avatarUrl: node.avatar?.source.url,
			});
		}
	}
	const placements = uniqueByUser(forChart.flatMap(({ placements }) => placements));
	const previousPlacements = uniqueByUser(
		forChart.flatMap(({ previousPlacements }) => previousPlacements),
	);
	const groupsByID = new Map(perGroup.map((group) => [group.group.id, group]));
	const employees = new Map<string, TalentReviewCellEmployee[]>();

	for (const placement of placements) {
		const group = groupsByID.get(placement.groupID);
		const member = group?.members.get(placement.userID);
		const employee: TalentReviewCellEmployee = {
			id: placement.userID,
			firstName: member?.firstName ?? '',
			lastName: member?.lastName ?? '',
			jobTitle: member?.jobTitle ?? '',
			departmentName: group?.group.name ?? '',
			managerName: member?.managerName || group?.departmentManager || '',
			xTick: localize(template.xAxis.ticks[placement.column])?.text ?? '',
			yTick: localize(template.yAxis.ticks[placement.row])?.text ?? '',
			lastPlaced: placement.localDate,
			avatar: member?.avatar ?? null,
		};
		const key = cellKey(placement.row, placement.column);
		const list = employees.get(key);

		if (list) {
			list.push(employee);
		} else {
			employees.set(key, [employee]);
		}
	}

	return {
		rows: toGridRows({
			template,
			currentCounts: countsFor(template.cells, placements),
			previousCounts: previousLocalDate ? countsFor(template.cells, previousPlacements) : undefined,
			employees,
			localize,
			deltaTooltip,
		}),
		xAxisLabel: localize(template.xAxis.label)?.text ?? '',
		yAxisLabel: localize(template.yAxis.label)?.text ?? '',
		hasNorm: template.hasNorm,
		placedCount: placements.length,
		eligibleCount: eligibleIDs.size,
		previousPlacedCount: previousPlacements.length,
		previousEligibleCount: eligibleIDs.size,
		departments: perGroup.map(({ group, eligibleIDs, placements }) => ({
			id: group.id,
			name: group.name,
			managerName: managerName(group.managers.edges),
			placedCount: placements.length,
			eligibleCount: eligibleIDs.size,
		})),
		retention: retentionRiskPeople(
			forChart.flatMap(({ group }) => source.writesByGroup.get(group.id) ?? []),
			localDate,
			eligibleIDs,
			users,
			new Map(forChart.map(({ group }) => [group.id, group.name])),
		),
	};
}

export function useTalentReviewAnalytics({
	templateID,
	groupIDs,
	tableGroupIDs,
	roleID,
	localDate,
	previousLocalDate,
}: {
	templateID?: string;
	/** Groups that count for the grid and the activity card. */
	groupIDs: string[];
	/**
	 * Groups shown in the progress table. Defaults to `groupIDs`. Extra ids
	 * (teams under the selected departments) are loaded for the table and
	 * left out of the grid and the activity card.
	 */
	tableGroupIDs?: string[];
	roleID?: string;
	localDate?: string;
	previousLocalDate?: string;
}): {
	loading: boolean;
	data?: TalentReviewAnalyticsResult;
} {
	const client = useApolloClient();
	const { requestQuery, lastSyncedAt } = useAnalytics();
	const { localize, language } = useLocalize();
	const { t } = useTranslation();
	const groupKey = groupIDs.join(',');
	const loadedKey = [...new Set([...(tableGroupIDs ?? groupIDs), ...groupIDs])].sort().join(',');
	// A new snapshot sync brings new placements, so it invalidates the source.
	const sourceKey = templateID && loadedKey ? `${templateID}|${loadedKey}|${lastSyncedAt}` : '';
	const [source, setSource] = useState<TalentReviewAnalyticsSource>();
	const [failedKey, setFailedKey] = useState<string>();

	useEffect(() => {
		if (!templateID || !loadedKey) {
			return;
		}

		const key = `${templateID}|${loadedKey}|${lastSyncedAt}`;
		let cancelled = false;

		loadTalentReviewAnalyticsSource({
			client,
			requestQuery,
			templateID,
			groupIDs: loadedKey.split(','),
		})
			.then((loaded) => {
				if (!cancelled) {
					setSource({ ...loaded, key });
				}
			})
			.catch(() => {
				if (!cancelled) {
					setFailedKey(key);
				}
			});

		return () => {
			cancelled = true;
		};
	}, [client, requestQuery, templateID, loadedKey, lastSyncedAt]);

	const current = source?.key === sourceKey ? source : undefined;

	const data = useMemo(
		() =>
			current && localDate
				? computeTalentReviewAnalytics({
						source: current,
						chartGroupIDs: groupKey ? groupKey.split(',') : [],
						roleID,
						localDate,
						previousLocalDate,
						localize,
						deltaTooltip: t('analytics.talentReview.grid.deltaTooltip'),
					})
				: undefined,
		// language.id recomputes after a locale switch. localize and t change
		// identity every render, so depending on them would recompute constantly.
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[current, groupKey, localDate, previousLocalDate, roleID, language.id],
	);

	const loading = Boolean(sourceKey && localDate) && !current && failedKey !== sourceKey;

	return { loading, data };
}
